import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfirmationType,
  Modules,
  NotificationDetails,
  NotificationHeader,
  NotificationTextMessage,
  RoutingPath,
} from '@app/core/Enum';
import { MainListParameters } from '@app/core/Models';
import { BankAccount, BankFeeds } from '@app/core/Models/bank/bank-account';
import { BankDasboardService, CommonService } from '@app/core/Services';
import {
  GetAllBankAccount,
  MenuState,
  RefreshBankAccountList,
} from '@app/core/Store';
import { ConfirmationBoxComponent } from '@app/modules/common';
import { Select, Store } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-bank-dashboard',
  templateUrl: './bank-dashboard.component.html',
  styleUrls: ['./bank-dashboard.component.scss'],
})
export class BankDashboardComponent implements OnInit {
  bankAccountData: Array<BankAccount>;
  ids: Array<Guid>;

  slideConfig = {
    slidesToShow: 3,
    slidesToScroll: 1,
    infinite: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  bankAccountDetailList: Array<BankFeeds>;
  subscriptionRouting: Subscription;
  isExpanded = true;
  selected;

  @Output()
  readonly triggerDataFromBank = new EventEmitter<any>();

  @Output()
  readonly triggerIsCustomFlag = new EventEmitter<any>();

  @Select(MenuState.moduleId)
  moduleId$: Observable<number>;
  moduleId: Modules;

  @Select(MenuState.getSelectedMenuPermission)
  isViewPermission$: Observable<any>;
  commonNotificationText = NotificationTextMessage;

  @Input() triggerHeaderChange: Observable<any>;
  @Input() triggerTabChange: Observable<any>;
  private destroy$ = new Subject<void>();
  listParameters: MainListParameters = new MainListParameters();
  isBankTabActive:boolean=true;
  @Input() selectedBankId: Guid;
  private readonly SELECTED_BANK_KEY = 'selectedBankId';
  constructor(
    private bankDasboardService: BankDasboardService,
    public dialog: MatDialog,
    public store: Store,
    private commonService: CommonService,
    private spinner: NgxSpinnerService,
    public datepipe: DatePipe,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.moduleId$.subscribe((moduleId) => {
      this.moduleId = moduleId;
      this.spinner.show();
      setTimeout(() => {
        this.getAllBankAccount(this.getParamter());
      }, 2000);
    });

    this.triggerHeaderChange
      ?.pipe(debounceTime(700), takeUntil(this.destroy$))
      .subscribe((data) => {
        this.listParameters = data;
        this.getAllBankAccount(this.getParamter());
      });
      this.triggerTabChange.subscribe((data) => {
        this.isBankTabActive = data;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getParamter(format?: number, isPrint?: boolean): any {
    let sdate : any = null;
    if(this.listParameters.startDate){
     sdate = this.datepipe
    .transform(this.listParameters.startDate, 'yyyy-MM-dd')
    ?.toString();
    }
    let edate : any = null;
    if(this.listParameters.endDate){
    edate = this.datepipe
    .transform(this.listParameters.endDate, 'yyyy-MM-dd')
    ?.toString();
    }
    const queryParams = {
      pageNumber: this.listParameters.pageNumber,
      pageSize: this.listParameters.pageSize,
      filter: this.listParameters.filter,
      sortBy: this.listParameters.sortBy,
      sortOrder: this.listParameters.sortOrder,
      search: this.listParameters.search,
      moduleId: this.listParameters.moduleId ?? (Guid.EMPTY as unknown as Guid),
      subModuleId: this.listParameters.subModuleId ?? -1,
      ids: this.ids ?? null,
      startDate: sdate,
      endDate: edate,
      format: format,
      isPrint: isPrint,
    };
    return queryParams;
  }

  getAllBankAccount(bankId?: any): void {
    this.store
      .dispatch(new GetAllBankAccount(this.getParamter()))
      .pipe(
        tap((res) => {
          this.bankAccountData = res.bankReconciliation.allBankAccount;

          if (!this.bankAccountData?.length) {
            return;
          }

          const storedBankId = localStorage.getItem(this.SELECTED_BANK_KEY);
          const storedGuid = storedBankId ? (storedBankId as unknown as Guid) : null;

          let selectedBank =
            this.bankAccountData.find(x => x.id === storedGuid) ||
            this.bankAccountData.find(x => x.id === this.selectedBankId) ||
            this.bankAccountData.find(x => x.id === bankId) ||
            this.bankAccountData.find(x => x.isDefault) ||
            this.bankAccountData[0];

          this.bankAccountData.forEach(acc => {
            acc.isDefault = false;
            acc.isCardSelected = false;
          });

          selectedBank.isDefault = true;
          selectedBank.isCardSelected = true;

          this.triggerDataFromBank.emit({
            id: selectedBank.id,
            isExpanded: this.isExpanded
          });
        })
      )
      .subscribe();
  }

  expandPanel(): void {
    this.isExpanded = !this.isExpanded;
    this.triggerData();
  }

  onAccountSelected(element: any, setDefault: boolean): void {
    this.bankAccountData.forEach((x) => {
      x.isCardSelected = x.id === element.id;
      x.isDefault = x.id === element.id;
    });
    localStorage.setItem(this.SELECTED_BANK_KEY, element.id);
    this.triggerData();
  }

  triggerData(): void {
    const selectedRecord = this.bankAccountData.find(
      (item) => item.isDefault === true
    );

    const data = {
      id: selectedRecord?.id,
      isExpanded: this.isExpanded,
    };

    this.triggerIsCustomFlag.emit(data);
    this.triggerDataFromBank.emit(data);
  }

  onAccountDelete(data: any): void {
    this.ids = [];
    this.ids.push(data.id);

    this.dialog
      .open(ConfirmationBoxComponent, {
        data: {
          ids: this.ids,
          type: ConfirmationType.Delete,
          moduleId: Modules.BankDashboard,
          headerText: NotificationHeader.deleteConfirmation,
          detailText: NotificationDetails.deleteAllDetailText,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          let isDefaultSelected;

          isDefaultSelected = this.bankAccountData.filter(
            (item: any) => item.isDefault === true && item.id !== data.id
          );
          let bankId;
          if (
            isDefaultSelected !== undefined &&
            isDefaultSelected !== null &&
            isDefaultSelected.length > 0
          ) {
            bankId = isDefaultSelected[0].id;
          }
          this.ids = [];
          this.listParameters.moduleId=undefined;
          this.getAllBankAccount(bankId);
        }
      });
  }

  onAutoBankReconcillationClick(element: any): void {
    const params = {
      id: btoa(element.id),
    };
    this.router.navigate([RoutingPath.AutoBankReconcillation, params]);
  }

  onAccountEdit(data: any): void {
    this.commonService.toggleMenu = false;
    this.commonService.onEditRouting(true, Modules.AddCustomAccounts, data.id);
  }
}
